import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { NetworkStatus } from '@apollo/client'
import { usePartnerContext } from '@retailer-platform/dashboard/utils'
import { type Account } from '../components/notifications-drawer/NotificationFeed'
import {
  NotificationSeverityEnum,
  NotificationScopeTypeEnum,
  type NotificationsNotification,
  type NotificationsNotificationEdge,
} from '../api'
import { type NotificationProps } from '../components/notifications-drawer/NotificationItem'
import { type NotificationTemplateType } from '../templates/TemplateRegistry'
import { convertKeysToCamelCaseDeep } from '../utils/camelCase'
import { useDomainMessages } from '../utils/domain/intl'
import { useNotificationsQuery } from './useNotificationsQuery'

export enum NotificationFilterType {
  All = 'all',
  Urgent = 'urgent',
  Unread = 'unread',
  Today = 'today',
  Earlier = 'earlier',
  TodayInfo = 'todayInfo',
  EarlierInfo = 'earlierInfo',
}

interface UseNotificationFeedOptions {
  filterType: NotificationFilterType
  limitInitialView?: boolean
  pageSize?: number
  pollingInterval?: number
  account: Account
}

interface PartnerContextType {
  warehouses?: Array<{ id: string | number; name: string }>
  partnerName?: string
  partner?: { id: string }
}

const getNotificationSource = (
  node: NotificationsNotification,
  partnerContext: PartnerContextType | null,
  i18n: {
    fallbackBanner: string
    fallbackPartner: string
    fallbackGlobal: string
  }
): string => {
  switch (node.scopeType) {
    case NotificationScopeTypeEnum.Banner: {
      // For banner notifications, use scopeId to find the warehouse/banner name
      if (node.scopeId && partnerContext?.warehouses) {
        const warehouse = partnerContext.warehouses.find(w => String(w.id) === String(node.scopeId))
        if (warehouse) {
          return warehouse.name
        }
      }
      return i18n.fallbackBanner
    }

    case NotificationScopeTypeEnum.Partner: {
      // For partner notifications, use the partner name from context
      if (partnerContext?.partnerName) {
        return partnerContext.partnerName
      }
      return i18n.fallbackPartner
    }

    case NotificationScopeTypeEnum.Global:
      // For global notifications, use channel name
      return node.channelName
  }
}

const getNotificationCategory = (
  scopeType: NotificationScopeTypeEnum,
  i18n: {
    categoryBanner: string
    categoryPartner: string
    categoryGlobal: string
  }
): string => {
  switch (scopeType) {
    case NotificationScopeTypeEnum.Banner:
      return i18n.categoryBanner
    case NotificationScopeTypeEnum.Partner:
      return i18n.categoryPartner
    case NotificationScopeTypeEnum.Global:
      return i18n.categoryGlobal
  }
}

const transformNotifications = (
  edges: NotificationsNotificationEdge[],
  account: Account,
  partnerContext: PartnerContextType | null,
  i18n: {
    fallbackBanner: string
    fallbackPartner: string
    fallbackGlobal: string
    categoryBanner: string
    categoryPartner: string
    categoryGlobal: string
  }
): NotificationProps[] =>
  edges
    .filter(
      (edge): edge is { node: NotificationsNotification; cursor: string } =>
        edge?.node != null && 'cursor' in edge
    )
    .map(({ node }) => {
      // Get template values, preferring app_context if it exists and has values
      const templateValues =
        node.templateValues.app_context && Object.keys(node.templateValues.app_context).length > 0
          ? convertKeysToCamelCaseDeep(node.templateValues.app_context)
          : convertKeysToCamelCaseDeep(node.templateValues.email_context?.body || {})

      const templateType = node.templateName.toUpperCase() as NotificationTemplateType
      const templateData = {
        // Account context that is provided "for free" for notifications
        givenName: account.givenName,
        surname: account.surname,
        email: account.email,
        id: account.id,
        // Add normalized template values
        ...templateValues,
      }
      return {
        id: node.id,
        title: node.channelName,
        source: getNotificationSource(node, partnerContext, {
          fallbackBanner: i18n.fallbackBanner,
          fallbackPartner: i18n.fallbackPartner,
          fallbackGlobal: i18n.fallbackGlobal,
        }),
        category: getNotificationCategory(node.scopeType, {
          categoryBanner: i18n.categoryBanner,
          categoryPartner: i18n.categoryPartner,
          categoryGlobal: i18n.categoryGlobal,
        }),
        isRead: node.isRead,
        severity: node.severity,
        createdAt: node.createdAt,
        templateType,
        templateData,
      }
    })
    .sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime()
      const dateB = new Date(b.createdAt).getTime()
      return dateB - dateA // Sort in descending order (newest first)
    })

const getTodayDateRange = () => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  return {
    startDate: today.toISOString(),
    endDate: tomorrow.toISOString(),
  }
}

const getEarlierDateRange = () => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return {
    startDate: new Date(0).toISOString(), // Beginning of time
    endDate: today.toISOString(),
  }
}

const getQueryParamsForFilter = (filterType: NotificationFilterType): Record<string, any> => {
  const todayRange = getTodayDateRange()
  const earlierRange = getEarlierDateRange()

  switch (filterType) {
    case NotificationFilterType.Urgent:
      return {
        severity: NotificationSeverityEnum.Urgent,
      }
    case NotificationFilterType.TodayInfo:
      return {
        severity: NotificationSeverityEnum.Info,
        startDate: todayRange.startDate,
        endDate: todayRange.endDate,
      }
    case NotificationFilterType.EarlierInfo:
      return {
        severity: NotificationSeverityEnum.Info,
        startDate: earlierRange.startDate,
        endDate: earlierRange.endDate,
      }
    case NotificationFilterType.Today:
      return {
        startDate: todayRange.startDate,
        endDate: todayRange.endDate,
      }
    case NotificationFilterType.Earlier:
      return {
        startDate: earlierRange.startDate,
        endDate: earlierRange.endDate,
      }
    case NotificationFilterType.Unread:
      return {
        isRead: false,
      }
    default:
      return {}
  }
}

// Main hook that accepts a filter type and returns the appropriate data
export const useNotificationFeed = (options: UseNotificationFeedOptions) => {
  const { filterType, limitInitialView = false, pageSize = 20, pollingInterval, account } = options
  const partnerContext = usePartnerContext()

  const i18n = useDomainMessages({
    fallbackBanner: 'notificationsDomain.feed.source.fallback.banner',
    fallbackPartner: 'notificationsDomain.feed.source.fallback.partner',
    fallbackGlobal: 'notificationsDomain.feed.source.fallback.global',
    categoryBanner: 'notificationsDomain.feed.category.banner',
    categoryPartner: 'notificationsDomain.feed.category.partner',
    categoryGlobal: 'notificationsDomain.feed.category.global',
  })

  const queryParams = getQueryParamsForFilter(filterType)

  const { data, loading, error, fetchMore, networkStatus, refetch } = useNotificationsQuery({
    ...queryParams,
    pageSize,
    notifyOnNetworkStatusChange: true,
    pollingInterval,
  })

  // Transform notifications
  const notifications = useMemo(() => {
    if (!data?.notifications?.edges) return []

    return transformNotifications(data.notifications.edges, account, partnerContext, i18n)
  }, [data, account, partnerContext, i18n])

  // Handle clearing notifications (simplified - just refetch)
  const handleNotificationCleared = useCallback(() => {
    refetch()
  }, [refetch])

  const hasMore = useMemo(() => {
    if (!data?.notifications?.pageInfo) return false
    return data.notifications.pageInfo.hasNextPage
  }, [data])

  const fetchMoreNotifications = useCallback(async () => {
    if (!data?.notifications?.pageInfo.hasNextPage) return

    await fetchMore({
      variables: {
        after: data.notifications.pageInfo.endCursor,
        ...queryParams,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult

        const previousQuery = previousResult.notifications
        const currentQuery = fetchMoreResult.notifications

        return {
          notifications: {
            ...currentQuery,
            edges: [...(previousQuery.edges || []), ...(currentQuery.edges || [])],
          },
        }
      },
    })
  }, [data, fetchMore, queryParams])

  const loadMore = useCallback(async () => {
    if (data?.notifications?.pageInfo.hasNextPage) {
      await fetchMoreNotifications()
    }
  }, [data, fetchMoreNotifications])

  return {
    notifications,
    loading: loading || networkStatus === NetworkStatus.fetchMore,
    error,
    hasMore,
    loadMore,
    totalCount: data?.notifications?.meta?.totalCount ?? 0,
    refetch,
    onNotificationCleared: handleNotificationCleared,
  }
}
